import { useCallback, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import { START_DATE_LAST_MONTH, TODAY_DATE } from '../../common/constants'
import theme from '../../ui/theme'
import ModalMobileUI from '../../ui/components/ModalMobile'
import SearchInput from '../../ui/modules/SearchShipmentsModal/SearchInput'
import Results from '../../ui/modules/SearchShipmentsModal/Results'

import useTranslations from '../../i18n/useTranslations'
import { useUserContext } from '../../auth/UserProvider'
import useNearElement from '../../hooks/useNearElement'
import { useInfiniteShippingsTracking } from '../../services/shippingsTracking'
import { parseShippingsTrackingResponse } from '../../services/shippingsTracking/business'

const STATIC_DEFAULT_FILTERS = {
  postalCodeSource: 'destin',
  dateFrom: START_DATE_LAST_MONTH,
  dateTo: TODAY_DATE,
}

const SearchShipmentsModal = ({ isOpen, onClose }) => {
  const t = useTranslations()
  const { isNearElement, visorRef } = useNearElement({
    once: false,
    distance: '200px',
  })
  const { clientId, statusList, centres } = useUserContext()

  const [inputSearch, setInputSearch] = useState('')

  const filters = {
    ...STATIC_DEFAULT_FILTERS,
    clientId,
    centres,
    search: inputSearch,
  }
  
  const queryConfig = {
    enabled: filters?.clientId !== undefined && inputSearch !== '',
  }

  const {
    data: searchResults,
    fetchNextPage,
    isFetching,
  } = useInfiniteShippingsTracking(filters, statusList, queryConfig, undefined, centres)

  const results = useMemo(
    () =>
      searchResults?.pages
        .map(page => page.data)
        ?.map(shipment => parseShippingsTrackingResponse(shipment, statusList))
        ?.map(shipment => shipment.shippings)
        ?.flat() ?? [],
    [searchResults?.pages, statusList],
  )

  const [showNotFoundWarning, setShowNotFoundWarning] = useState(false)

  const handleSearch = code => {
    setInputSearch(code)
  }
  const resetPrevSearchFeedback = () => {
    setShowNotFoundWarning(false)
  }
  const resetSearch = useCallback(() => {
    resetPrevSearchFeedback()
    setInputSearch('')
  }, [])

  const handleClose = useCallback(() => {
    onClose()
    setTimeout(() => resetSearch(), theme.transitions.duration.leavingScreen)
  }, [onClose, resetSearch])

  useEffect(() => {
    if (inputSearch !== '' && !isFetching && results?.length === 0) {
      setShowNotFoundWarning(true)
    } else {
      setShowNotFoundWarning(false)
    }
  }, [results, inputSearch, isFetching])

  //FIX - IS NOT WORKING INFINITE QUERY
  useEffect(() => {
    if (isNearElement) fetchNextPage()
  }, [isNearElement, fetchNextPage])

  const showSearchInput = !results || !results?.length

  const title = showSearchInput
    ? t('home:find-shipment')
    : t('home:result-shipment')

  return (
    <ModalMobileUI
      title={title}
      isOpen={isOpen}
      onClose={handleClose}
      isFullHeight={!showSearchInput}
      anchor="top"
    >
      <>
        {showSearchInput ? (
          <SearchInput
            onSearch={handleSearch}
            onChangeInput={resetPrevSearchFeedback}
            showNotFoundWarning={showNotFoundWarning}
          />
        ) : (
          <Results data={results} />
        )}
        <div id="end-page-visor" ref={visorRef} />
      </>
    </ModalMobileUI>
  )
}

SearchShipmentsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

export default SearchShipmentsModal
